import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkle, FileText, CheckCircle, Warning, ArrowLeft, ArrowRight, Download } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  { value: 'rehabilitation', label: 'Réhabilitation' },
  { value: 'mise_en_securite', label: 'Mise en sécurité / Péril' },
  { value: 'construction_neuve', label: 'Construction neuve' },
  { value: 'extension', label: 'Extension' },
  { value: 'renovation', label: 'Rénovation' },
  { value: 'amenagement', label: 'Aménagement intérieur' },
  { value: 'changement_usage', label: "Changement d'usage" },
];

const PRIORITE_COLORS = {
  URGENT: 'bg-[#FFD7CC] text-[#C75B39] border-[#C75B39]',
  'NÉCESSAIRE': 'bg-[#FFF3CD] text-[#B8860B] border-[#D4A84B]',
  RECOMMANDÉ: 'bg-[#D4EDDA] text-[#2D6A4F] border-[#2D6A4F]',
};

function formatEur(n) {
  if (n == null) return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  if (Number.isNaN(v)) return '—';
  return v.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

export default function DPGFGenerateAIDialog({ open, onOpenChange, chantierId, onImported }) {
  // step: 1=select docs, 2=instructions, 3=generating, 4=result
  const [step, setStep] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [typeProjet, setTypeProjet] = useState('rehabilitation');
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [proposition, setProposition] = useState(null);
  const [selectedLots, setSelectedLots] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const resetAll = useCallback(() => {
    setStep(1);
    setSelectedDocIds(new Set());
    setInstructions('');
    setTypeProjet('rehabilitation');
    setProposition(null);
    setSelectedLots(new Set());
    setGenerating(false);
    setImporting(false);
  }, []);

  // Fetch documents on open
  useEffect(() => {
    if (!open) return;
    resetAll();
    const fetchDocs = async () => {
      setLoadingDocs(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/documents/${chantierId}`, { withCredentials: true });
        const docs = Array.isArray(data) ? data : (data?.documents || []);
        setDocuments(docs);
        // Pre-select docs that have OCR text
        const preSel = new Set(docs.filter(d => d.texte_extrait_length > 0 || d.has_ocr).map(d => d._id || d.id));
        setSelectedDocIds(preSel);
      } catch (err) {
        toast.error("Impossible de charger les documents");
      } finally {
        setLoadingDocs(false);
      }
    };
    fetchDocs();
  }, [open, chantierId, resetAll]);

  // Check if an existing AI proposition is already stored (optional: offer to reopen)
  useEffect(() => {
    if (!open) return;
    const fetchExisting = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf/generee-ia`, { withCredentials: true });
        if (data?.dpgf_generee_ia?.lots?.length) {
          // Silent: don't auto-open, just leave available via a button on step 1
          // Store it so user can reopen via button
        }
      } catch { /* ignore */ }
    };
    fetchExisting();
  }, [open, chantierId]);

  const toggleDoc = (id) => {
    setSelectedDocIds(prev => {
      const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
    });
  };

  const handleGenerate = async () => {
    setStep(3);
    setGenerating(true);
    try {
      const body = {
        document_ids: Array.from(selectedDocIds),
        type_projet: typeProjet,
        instructions: instructions.trim(),
      };
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/generer-depuis-documents`,
        body,
        { withCredentials: true, timeout: 180000 }
      );
      setProposition(data);
      // Pre-select all lots
      setSelectedLots(new Set((data?.lots || []).map(l => l.numero)));
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération IA');
      setStep(2);
    } finally {
      setGenerating(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/importer-ia`,
        { lots: Array.from(selectedLots) },
        { withCredentials: true }
      );
      toast.success(data.message || 'DPGF importée');
      onImported?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  const toggleLot = (numero) => {
    setSelectedLots(prev => {
      const n = new Set(prev); if (n.has(numero)) n.delete(numero); else n.add(numero); return n;
    });
  };

  const docsWithOcr = documents.filter(d => d.texte_extrait_length > 0 || d.has_ocr);
  const canProceedFromStep1 = selectedDocIds.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dpgf-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <Sparkle size={22} className="text-[#C75B39]" weight="fill" />
            Générer la DPGF depuis les documents
          </DialogTitle>
          {/* Stepper */}
          <div className="flex items-center gap-2 pt-3 text-xs">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step >= s ? 'bg-[#1E3A5F] text-white' : 'bg-[#E0E0DB] text-[#6B6B6B]'}`}>
                  {step > s ? <CheckCircle size={14} weight="fill" /> : s}
                </div>
                <span className={step >= s ? 'text-[#2C2C2C] font-medium' : 'text-[#9A9A9A]'}>
                  {s === 1 ? 'Documents' : s === 2 ? 'Instructions' : s === 3 ? 'Génération' : 'Validation'}
                </span>
                {s < 4 && <span className="text-[#E0E0DB]">—</span>}
              </div>
            ))}
          </div>
        </DialogHeader>

        {/* ===== STEP 1: Select documents ===== */}
        {step === 1 && (
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold text-[#2C2C2C] uppercase tracking-wider">Type de projet</Label>
              <Select value={typeProjet} onValueChange={setTypeProjet}>
                <SelectTrigger className="rounded-none mt-1" data-testid="dpgf-ai-type-projet"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-[#2C2C2C] uppercase tracking-wider">
                  Documents à analyser ({selectedDocIds.size} sélectionné{selectedDocIds.size > 1 ? 's' : ''} / {docsWithOcr.length} analysés)
                </Label>
                {docsWithOcr.length > 0 && (
                  <button
                    className="text-xs text-[#1E3A5F] hover:underline"
                    onClick={() => {
                      if (selectedDocIds.size === docsWithOcr.length) setSelectedDocIds(new Set());
                      else setSelectedDocIds(new Set(docsWithOcr.map(d => d._id || d.id)));
                    }}
                    data-testid="dpgf-ai-select-all-docs"
                  >
                    {selectedDocIds.size === docsWithOcr.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                )}
              </div>
              {loadingDocs ? (
                <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-[#E0E0DB] bg-[#FAFAF9]">
                  <FileText size={36} className="text-[#9A9A9A] mx-auto mb-2" />
                  <p className="text-sm text-[#6B6B6B]">Aucun document dans ce chantier.</p>
                  <p className="text-xs text-[#9A9A9A] mt-1">Uploadez vos documents (arrêté, rapport d'expertise, diagnostic...) avant de générer.</p>
                </div>
              ) : (
                <div className="border border-[#E0E0DB] divide-y divide-[#F0F0EE] max-h-64 overflow-y-auto">
                  {documents.map(doc => {
                    const id = doc._id || doc.id;
                    const hasOcr = (doc.texte_extrait_length > 0) || doc.has_ocr;
                    const selected = selectedDocIds.has(id);
                    return (
                      <label
                        key={id}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${!hasOcr ? 'opacity-50 cursor-not-allowed' : selected ? 'bg-[#EFF6FF]' : 'hover:bg-[#FAFAF9]'}`}
                        data-testid={`dpgf-ai-doc-${id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={!hasOcr}
                          onChange={() => hasOcr && toggleDoc(id)}
                          className="accent-[#1E3A5F]"
                        />
                        <FileText size={18} className={hasOcr ? 'text-[#1E3A5F]' : 'text-[#9A9A9A]'} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#2C2C2C] truncate">{doc.name || doc.filename}</p>
                          <p className="text-[10px] text-[#9A9A9A]">
                            {doc.type || 'Document'} · {doc.size ? `${Math.round(doc.size / 1024)} Ko` : ''}
                            {hasOcr ? <span className="text-[#2D6A4F] ml-2">· Analysé OCR ({doc.texte_extrait_length || '?'} car.)</span>
                              : <span className="text-[#C75B39] ml-2">· Non analysé — lancez l&apos;OCR</span>}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {docsWithOcr.length === 0 && documents.length > 0 && (
                <div className="mt-2 flex items-start gap-2 p-3 bg-[#FFF3CD] border border-[#D4A84B] text-xs text-[#6B6B6B]">
                  <Warning size={16} className="text-[#D4A84B] flex-shrink-0 mt-0.5" />
                  <span>Aucun document n&apos;a de texte extrait. Lancez d&apos;abord l&apos;analyse OCR dans l&apos;onglet Documents, puis revenez ici.</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Annuler</Button>
              <Button
                disabled={!canProceedFromStep1}
                onClick={() => setStep(2)}
                className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none gap-2"
                data-testid="dpgf-ai-next-1"
              >
                Suivant <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* ===== STEP 2: Instructions ===== */}
        {step === 2 && (
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold text-[#2C2C2C] uppercase tracking-wider">
                Instructions pour l&apos;IA (optionnel)
              </Label>
              <Textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                rows={5}
                placeholder="Ex: Priorité sur la mise en sécurité structurelle. Budget cible 200k€ HT. Exclure les travaux de second œuvre. Inclure un lot Honoraires avec assurance DO."
                className="rounded-none mt-1 text-sm"
                data-testid="dpgf-ai-instructions"
              />
              <p className="text-xs text-[#9A9A9A] mt-1">
                L&apos;IA analysera {selectedDocIds.size} document{selectedDocIds.size > 1 ? 's' : ''} pour générer une DPGF complète (lots, postes, quantités, prix estimés).
              </p>
            </div>

            <div className="bg-[#F5F5F3] border border-[#E0E0DB] p-3 text-xs text-[#6B6B6B] space-y-1">
              <p className="font-semibold text-[#2C2C2C]">⏱ Temps estimé : 30 à 90 secondes</p>
              <p>L&apos;IA génère une désignation technique détaillée, avec quantités estimées, prix unitaires marché 2026 et justification citant le document source.</p>
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-none gap-2">
                <ArrowLeft size={14} /> Retour
              </Button>
              <Button
                onClick={handleGenerate}
                className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2"
                data-testid="dpgf-ai-generate"
              >
                <Sparkle size={14} weight="fill" /> Générer la DPGF
              </Button>
            </div>
          </div>
        )}

        {/* ===== STEP 3: Generating ===== */}
        {step === 3 && (
          <div className="py-12 text-center space-y-4" data-testid="dpgf-ai-generating">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C75B39] animate-pulse">
              <Sparkle size={32} className="text-white" weight="fill" />
            </div>
            <h3 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
              L&apos;IA analyse {selectedDocIds.size} document{selectedDocIds.size > 1 ? 's' : ''}...
            </h3>
            <p className="text-sm text-[#6B6B6B] max-w-md mx-auto">
              Génération de la DPGF en cours. Cela peut prendre jusqu&apos;à 90 secondes.<br />
              Merci de ne pas fermer cette fenêtre.
            </p>
            <div className="flex justify-center gap-1">
              <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        )}

        {/* ===== STEP 4: Result ===== */}
        {step === 4 && proposition && (
          <div className="space-y-4 mt-2">
            {/* Résumé */}
            <div className="border border-[#1E3A5F] bg-[#F5F5F3] p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-[#2C2C2C] mb-1" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    {proposition.page_de_garde?.titre || 'DPGF proposée'}
                  </h3>
                  <p className="text-sm text-[#4A4A4A]">{proposition.resume || '—'}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Budget estimé HT</p>
                  <p className="text-2xl font-bold text-[#C75B39]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    {formatEur(proposition.budget_estime_ht)}
                  </p>
                </div>
              </div>
              {proposition.recapitulatif_par_delai && (
                <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                  <div className="bg-white border border-[#C75B39] p-2">
                    <span className="text-[#C75B39] font-semibold">🟢 10 jours</span> : {formatEur(proposition.recapitulatif_par_delai.travaux_10_jours_ht)}
                  </div>
                  <div className="bg-white border border-[#D4A84B] p-2">
                    <span className="text-[#B8860B] font-semibold">◻ Moyen terme</span> : {formatEur(proposition.recapitulatif_par_delai.travaux_moyen_terme_ht)}
                  </div>
                </div>
              )}
            </div>

            {/* Lots */}
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-[#2C2C2C] uppercase tracking-wider">
                Lots proposés ({selectedLots.size}/{proposition.lots?.length || 0} sélectionnés)
              </Label>
              <button
                className="text-xs text-[#1E3A5F] hover:underline"
                onClick={() => {
                  if (selectedLots.size === proposition.lots.length) setSelectedLots(new Set());
                  else setSelectedLots(new Set(proposition.lots.map(l => l.numero)));
                }}
                data-testid="dpgf-ai-select-all-lots"
              >
                {selectedLots.size === proposition.lots.length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
            </div>

            <div className="border border-[#E0E0DB] divide-y divide-[#F0F0EE] max-h-96 overflow-y-auto">
              {(proposition.lots || []).map(lot => {
                const selected = selectedLots.has(lot.numero);
                const priorite = (lot.priorite || '').toUpperCase();
                return (
                  <div
                    key={lot.numero}
                    className={`p-3 transition-colors ${selected ? 'bg-[#EFF6FF]' : 'bg-white'}`}
                    data-testid={`dpgf-ai-lot-${lot.numero}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleLot(lot.numero)}
                        className="accent-[#1E3A5F] mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-[#2C2C2C]">{lot.numero} — {lot.nom}</span>
                          {lot.corps_etat && <span className="text-[10px] text-[#6B6B6B] italic">({lot.corps_etat})</span>}
                          {priorite && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 border ${PRIORITE_COLORS[priorite] || 'bg-[#F5F5F3] text-[#6B6B6B] border-[#E0E0DB]'}`}>
                              {priorite}
                            </span>
                          )}
                          {lot.delai && (
                            <span className="text-[10px] font-bold text-[#1E3A5F] bg-[#EFF6FF] px-2 py-0.5 border border-[#1E3A5F]">
                              {lot.delai}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#6B6B6B] mt-0.5">
                          {(lot.postes || []).length} poste{(lot.postes || []).length > 1 ? 's' : ''} · <strong className="text-[#2C2C2C]">{formatEur(lot.montant_estime_ht)}</strong>
                        </p>
                      </div>
                    </div>
                    {/* Postes */}
                    {(lot.postes || []).length > 0 && (
                      <div className="mt-2 ml-6 space-y-1.5">
                        {(lot.postes || []).slice(0, 10).map((p, idx) => (
                          <div key={idx} className="text-xs border-l-2 border-[#E0E0DB] pl-3 py-0.5">
                            <div className="flex items-start gap-2">
                              <span className="text-[#9A9A9A] font-mono">{p.numero || `${idx + 1}.`}</span>
                              <div className="flex-1">
                                <p className="text-[#2C2C2C]">{p.designation}</p>
                                <p className="text-[10px] text-[#6B6B6B] mt-0.5">
                                  {p.quantite || 0} {p.unite || 'ens.'} × {formatEur(p.prix_unitaire_ht)} = <strong className="text-[#1E3A5F]">{formatEur(p.montant_ht || (p.quantite || 0) * (p.prix_unitaire_ht || 0))}</strong>
                                </p>
                                {p.justification && (
                                  <p className="text-[10px] text-[#9A9A9A] italic mt-0.5">↳ {p.justification}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {(lot.postes || []).length > 10 && (
                          <p className="text-[10px] text-[#9A9A9A] italic ml-5">
                            ... et {lot.postes.length - 10} autre{lot.postes.length - 10 > 1 ? 's' : ''} poste{lot.postes.length - 10 > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {proposition.conditions_particulieres && (
              <details className="border border-[#E0E0DB] p-3 text-xs text-[#6B6B6B] bg-[#FAFAF9]">
                <summary className="font-semibold text-[#2C2C2C] cursor-pointer">Conditions particulières</summary>
                <p className="mt-2 whitespace-pre-wrap">{proposition.conditions_particulieres}</p>
              </details>
            )}

            <div className="flex justify-between gap-2 pt-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => window.open(`${API_URL}/api/chantiers/${chantierId}/dpgf/export-xlsx-ia`, '_blank')}
                className="rounded-none gap-2"
                data-testid="dpgf-ai-export-xlsx"
              >
                <Download size={14} /> Télécharger Excel (2 onglets)
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-none">
                  Régénérer
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">
                  Annuler
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={importing || selectedLots.size === 0}
                  className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none gap-2"
                  data-testid="dpgf-ai-import"
                >
                  {importing ? 'Import...' : `Importer ${selectedLots.size} lot${selectedLots.size > 1 ? 's' : ''} dans la DPGF`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
